import React from 'react';
import { Check, Gift } from 'lucide-react';
import { Section } from './Shared';
export const MethodOverview = () => {
  const tomos = [{
    color: 'bg-purple-900/40 border-purple-500/30',
    icon: '🟣',
    title: 'TOMO I — El Volcado de Emergencia',
    subtitle: 'La técnica de escritura akáshica que vacía tu mente en 7 minutos',
    desc: 'Cuando la cabeza no para y no sabés ni por dónde empezar. Una técnica de escritura guiada para vaciar el ruido mental, ordenar el caos interno y crear el espacio necesario para empezar a sanar de verdad.',
    outcome: 'Apagar el loop mental y recuperar la claridad en minutos, no en meses.'
  }, {
    color: 'bg-blue-900/40 border-blue-500/30',
    icon: '🔵',
    title: 'TOMO II — Limpieza de Vínculos Agotadores',
    subtitle: 'El protocolo akáshico para soltar las personas que te drenan la energía',
    desc: 'Ex parejas, amistades tóxicas, familiares que te vaciaron. El protocolo exacto para cortar los cordones energéticos que todavía te unen a ellas y recuperar toda la energía que entregaste.',
    outcome: 'Terminar el día con energía propia, sin cargar con lo de los demás.'
  }, {
    color: 'bg-emerald-900/40 border-emerald-500/30',
    icon: '🟢',
    title: 'TOMO III — El Detector de Mentiras Propias',
    subtitle: 'El protocolo akáshico para desarmar las creencias que te frenan todos los días',
    desc: '¿Cuántas de tus decisiones diarias las toma una creencia que aprendiste a los 7 años? Identificá las mentiras que operan en automático y desmantelalas desde la raíz akáshica.',
    outcome: 'Tomar decisiones desde vos misma, no desde el miedo heredado.'
  }, {
    color: 'bg-slate-900/60 border-slate-500/30',
    icon: '🌙',
    title: 'TOMO IV — Protocolo Apagar la Mente',
    subtitle: 'La secuencia akáshica nocturna que silencia el loop mental y te devuelve el sueño',
    desc: 'Si te acostás y tu mente empieza a repasar todo lo que hiciste mal y todo lo que podría salir mal. Una secuencia específica para antes de dormir que limpia el campo del día.',
    outcome: 'Levantarte descansada y con el campo ordenado para enfrentar el día.'
  }, {
    color: 'bg-rose-900/40 border-rose-500/30',
    icon: '🍷',
    title: 'TOMO V — El Abrazo a Tu Sombra',
    subtitle: 'El protocolo akáshico para reconciliarte con las partes de vos que escondiste',
    desc: 'Todo lo que mandaste al sótano interno sigue operando desde ahí, saboteando tus relaciones y tus decisiones. Encontrate con esas partes, entendelas e integralas.',
    outcome: 'Dejar de autosabotearte y relacionarte con vos misma desde la compasión real.'
  }, {
    color: 'bg-green-900/40 border-green-500/30',
    icon: '🌲',
    title: 'TOMO VI — El Mapa del Próximo Paso Real',
    subtitle: 'El protocolo akáshico para pasar de la parálisis a la acción desde un campo limpio',
    desc: 'Después de limpiar, vaciar, soltar e integrar, llega el momento de avanzar. El mapa para identificar tu próximo paso real y darlo con certeza.',
    outcome: 'Saber exactamente hacia dónde ir y empezar a moverte sin miedo.'
  }];
  const checklist = ['Protocolo akáshico paso a paso listo para aplicar', 'Ejercicios de escritura terapéutica guiados', 'Técnicas de visualización y limpieza energética', 'Preguntas de cierre para profundizar el proceso', 'Marco teórico akáshico y holístico explicado con claridad', 'Conexión entre cada tomo para un proceso secuencial real', '100% diseñado para aplicar sola, a tu ritmo'];
  return <Section id="method" data-id="element-149">
      <div className="text-center mb-16" data-id="element-150">
        <div className="inline-flex items-center space-x-2 bg-brand-surface border border-brand-gold/30 px-4 py-1.5 rounded-full mb-6" data-id="element-151">
          <span className="text-xl" data-id="element-152">🌟</span>
          <span className="font-serif font-semibold tracking-wider text-brand-gold text-sm md:text-base" data-id="element-153">
            MÉTODO BORRÓN Y CUENTA NUEVA
          </span>
        </div>
        <h2 className="text-3xl md:text-5xl font-serif font-bold mb-4" data-id="element-154">
          6 Tomos + 2 Bonus — Sistema Completo de Sanación Akáshica
        </h2>
        <p className="text-xl text-brand-muted" data-id="element-155">
          180+ páginas de protocolos listos para aplicar. Todo resuelto.
        </p>
      </div>

      <h3 className="text-2xl font-serif font-bold text-center mb-10 gold-gradient-text" data-id="element-156">
        6 TOMOS COMPLETOS
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16" data-id="element-157">
        {tomos.map((tomo, i) => <div key={i} className={`rounded-2xl p-6 md:p-8 border ${tomo.color} backdrop-blur-sm`} data-id="element-158">
            <div className="text-3xl mb-4" data-id="element-159">{tomo.icon}</div>
            <h4 className="text-xl font-serif font-bold mb-2" data-id="element-160">{tomo.title}</h4>
            <p className="text-brand-gold/90 font-medium text-sm mb-4" data-id="element-161">
              {tomo.subtitle}
            </p>
            <p className="text-brand-text/80 text-sm leading-relaxed mb-6" data-id="element-162">
              {tomo.desc}
            </p>
            <div className="bg-black/20 rounded-lg p-4 border border-white/5" data-id="element-163">
              <p className="text-sm" data-id="element-164">
                <span className="font-bold text-brand-gold" data-id="element-165">
                  Lo que lográs:
                </span>{' '}
                {tomo.outcome}
              </p>
            </div>
          </div>)}
      </div>

      <div className="glass-panel rounded-3xl p-8 md:p-12 max-w-3xl mx-auto mb-24" data-id="element-166">
        <h4 className="text-xl font-serif font-bold mb-6 text-center" data-id="element-167">
          Cada tomo incluye:
        </h4>
        <ul className="space-y-4" data-id="element-168">
          {checklist.map((item, i) => <li key={i} className="flex items-start" data-id="element-169">
              <Check className="w-5 h-5 text-brand-gold mr-3 mt-0.5 shrink-0" data-id="element-170" />
              <span className="text-brand-text/90" data-id="element-171">{item}</span>
            </li>)}
        </ul>
      </div>

      <div className="text-center mb-12" data-id="element-172">
        <h3 className="text-3xl md:text-4xl font-serif font-bold mb-4" data-id="element-173">
          🎁 2 BONUS ESTRATÉGICOS
        </h3>
        <p className="text-brand-muted text-lg" data-id="element-174">
          Siempre incluidos. Siempre gratis.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-175">
        <div className="glass-panel rounded-2xl p-8 border-brand-gold/30 relative overflow-hidden" data-id="element-176">
          <div className="absolute top-0 right-0 bg-brand-gold text-brand-dark text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-177">
            GRATIS
          </div>
          <Gift className="w-10 h-10 text-brand-gold mb-4" data-id="element-178" />
          <h4 className="text-xl font-serif font-bold mb-2" data-id="element-179">
            BONUS 1 — 40 Disparadores Akáshicos
          </h4>
          <p className="text-brand-gold/90 text-sm font-medium mb-4" data-id="element-180">
            Qué escribir cuando no sabés por dónde empezar
          </p>
          <p className="text-brand-text/80 text-sm mb-6" data-id="element-181">
            La hoja en blanco es el enemigo número uno del proceso de sanación.
            40 preguntas organizadas por emoción para que nunca te quedes sin
            saber qué escribir en tus días de niebla mental.
          </p>
          <div className="text-sm" data-id="element-182">
            <span className="line-through text-brand-text/50 mr-2" data-id="element-183">
              Valor real: $5.000
            </span>{' '}
            <span className="text-brand-gold font-bold" data-id="element-184">
              → GRATIS con tu pack
            </span>
          </div>
        </div>

        <div className="glass-panel rounded-2xl p-8 border-brand-gold/30 relative overflow-hidden" data-id="element-185">
          <div className="absolute top-0 right-0 bg-brand-gold text-brand-dark text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-186">
            GRATIS
          </div>
          <Gift className="w-10 h-10 text-brand-gold mb-4" data-id="element-187" />
          <h4 className="text-xl font-serif font-bold mb-2" data-id="element-188">
            BONUS 2 — Protocolo de Blindaje Energético
          </h4>
          <p className="text-brand-gold/90 text-sm font-medium mb-4" data-id="element-189">
            Cómo mantener tu campo limpio en entornos difíciles
          </p>
          <p className="text-brand-text/80 text-sm mb-6" data-id="element-190">
            De nada sirve limpiar el campo si lo volvés a ensuciar al día
            siguiente. Herramientas concretas para proteger tu energía antes,
            durante y después de entornos y personas que te drenan.
          </p>
          <div className="text-sm" data-id="element-191">
            <span className="line-through text-brand-text/50 mr-2" data-id="element-192">
              Valor real: $5.000
            </span>{' '}
            <span className="text-brand-gold font-bold" data-id="element-193">
              → GRATIS con tu pack
            </span>
          </div>
        </div>
      </div>

      <div className="text-center mt-8" data-id="element-194">
        <p className="text-lg font-medium bg-brand-surfaceLight/50 inline-block px-6 py-3 rounded-full border border-brand-gold/20" data-id="element-195">
          Valor total de los Bonus:{' '}
          <span className="line-through text-brand-text/50 mx-1" data-id="element-196">$10.000</span>{' '}
          <span className="text-brand-gold font-bold" data-id="element-197">
            → GRATIS con tu Método
          </span>
        </p>
      </div>
    </Section>;
};